/**
 * `site-compliance` — thin dashboard client for `PUT /v1/sites/:site_id/
 * compliance` (ADR-0127 Slice 1).
 *
 * The existing catalog api client at `apps/dashboard/src/api/catalog.ts`
 * already exposes `putCompliance(http, site_id, update)` — the function
 * used by the Catalog view's compliance form. ADR-0127 makes the three
 * compliance fields ALSO editable from the Visibility view, so the
 * shared SuggestionsModal dispatches a save for those three fields
 * through THIS module. The wrapper is a one-liner that delegates to
 * `putCompliance` and re-types the result with the discriminators the
 * modal branches on; it intentionally does NOT re-implement the wire
 * shape or duplicate the route logic.
 *
 * Wire contract recap (mirrors the server route at
 * `services/api/src/http/routes/v1/catalog.ts:1530-1620`):
 *
 *   PUT /v1/sites/:site_id/compliance
 *     body: { seller_tos_url?, seller_privacy_policy_url?, returns_policy? }
 *     200 → { site_id, account_id, ...fields } — the merged row
 *     400 → ApiError('bad_request') with `server_reason` carrying the
 *           offending field (e.g. `invalid_seller_tos_url`)
 *     403 → ApiError('forbidden') — reviewer/viewer role
 *     404 → ApiError('not_found') — cross-tenant / unknown site
 *
 * Concurrency: NO eTag. The route's `upsertSiteCompliance` is a
 * partial-merge by construction — a string sets it, `null` clears it,
 * `undefined`/absent leaves it unchanged. Last-write-wins on
 * same-field-same-time is the existing reality and is documented in
 * ADR-0127 §4.
 *
 * Security review surface (ADR-0127): this module adds a SECOND React
 * caller of the compliance write route. The route is already RLS-scoped
 * + role-gated server-side; the new caller does not bypass either. The
 * payload shape is identical to the Catalog view's existing caller.
 */

import { putCompliance, type ComplianceUpdate } from './catalog.js';
import type { HttpClient } from './http-client.js';

/**
 * Discriminated-union result. The shared SuggestionsModal branches on
 * `kind` to choose between "close + lime toast" (ok), "close + coral
 * toast" (forbidden / not_found), "stay open + inline banner"
 * (validation), and "close + coral toast" (unknown).
 *
 * `validation` carries the server's `reason` discriminator unchanged
 * so the modal's existing inline-error path renders the same copy as
 * a 400 from the agent-card-profile route.
 */
export type UpdateSiteComplianceResult =
  | { readonly kind: 'ok' }
  | { readonly kind: 'forbidden' }
  | { readonly kind: 'not_found' }
  | { readonly kind: 'validation'; readonly server_reason?: string }
  | { readonly kind: 'unknown'; readonly message?: string };

/**
 * The three writable compliance fields. Identical to the Catalog
 * view's `ComplianceUpdate` shape because both views target the same
 * route — the type is re-exported here for callers that don't already
 * import from the catalog api module.
 */
export type SiteComplianceUpdate = ComplianceUpdate;

/**
 * Update one or more compliance fields. `undefined`/absent leaves the
 * field unchanged, an explicit `null` clears it, and a non-empty
 * string sets it. The server validates URLs (`https://` only) and
 * rejects an empty `returns_policy` string with a 400; treat empty as
 * `null` if the caller intends "clear" (matches the route's
 * empty-vs-null semantics described in `catalog.ts:1554-1556`).
 */
export async function updateSiteCompliance(
  http: HttpClient,
  site_id: string,
  update: SiteComplianceUpdate,
): Promise<UpdateSiteComplianceResult> {
  const result = await putCompliance(http, site_id, update);
  switch (result.kind) {
    case 'ok':
      return { kind: 'ok' };
    case 'forbidden':
      return { kind: 'forbidden' };
    case 'not_found':
      return { kind: 'not_found' };
    case 'bad_request':
      return result.reason !== undefined
        ? { kind: 'validation', server_reason: result.reason }
        : { kind: 'validation' };
    case 'error':
      return { kind: 'unknown', message: result.message };
    default: {
      // Exhaustiveness guard — surfaces a new kind from `putCompliance`
      // as a typecheck failure rather than silently mapping to unknown.
      const _exhaustive: never = result;
      void _exhaustive;
      return { kind: 'unknown' };
    }
  }
}
